import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CSS_RESTRICTIONS } from '@common/constants';
import type { TypographySettingsFormGroup } from '@web/widgets/product-card/type';
import { type TypographySettingsFormType } from '@web/widgets/product-card/type';
import { EFontStyle } from '@common/enums';
import { type FormArray, FormBuilder } from '@angular/forms';
import { type FontStyleFormControl } from '@web/widgets/product-card/type/product-card-form.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { TranslateModule } from '@ngx-translate/core';
import { SpSwitchComponent } from '@ui/sp-switch';
import { UpperCasePipe } from '@angular/common';
import { SpColorPickerModule } from '@components/sp-color-picker/src/sp-color-picker.module';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { ThemeStateService } from '@sites/site-theme/src/state';

enum SettingsType {
    title,
    description,
    price,
    oldPrice,
}

@Component({
    selector: 'web-typography-settings',
    templateUrl: './typography-settings.component.html',
    styleUrls: ['./typography-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpInputNumComponent, SpColorPickerModule, SpSwitchComponent, UpperCasePipe, TranslateModule],
})
export class TypographySettingsComponent {
    protected readonly fb = inject(FormBuilder);
    private readonly themeState = inject(ThemeStateService);

    @Input() typographySettingsFormGroup: TypographySettingsFormGroup;
    @Input() isPreviewSettings: boolean = false;

    public settingsType = SettingsType;
    protected readonly EFontStyle = EFontStyle;
    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setFontSize(settingsType: SettingsType, value: number) {
        switch (settingsType) {
            case SettingsType.title:
                this.titleSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.description:
                this.descriptionSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.price:
                this.priceSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('fontSize').patchValue(value);
                break;
        }
    }

    public isActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): boolean {
        switch (settingsType) {
            case SettingsType.title:
                return this.titleSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.description:
                return this.descriptionSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.price:
                return this.priceSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.oldPrice:
                return this.oldPriceSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
        }
    }

    public toggleActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): void {
        switch (settingsType) {
            case SettingsType.title:
                this.setFontStyle(
                    this.titleSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.description:
                this.setFontStyle(
                    this.descriptionSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.price:
                this.setFontStyle(
                    this.priceSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.oldPrice:
                this.setFontStyle(
                    this.oldPriceSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
        }
    }

    public setTextColor(settingsType: SettingsType, { value }: ColorPickerEventPayload): void {
        switch (settingsType) {
            case SettingsType.title:
                this.titleSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.description:
                this.descriptionSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.price:
                this.priceSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('color').patchValue(value);
                break;
        }
    }

    public setIsShowText(settingsType: SettingsType, value: boolean) {
        switch (settingsType) {
            case SettingsType.description:
                this.descriptionSettingsFormGroup.get('isShow').patchValue(value);
                break;
            case SettingsType.price:
                this.priceSettingsFormGroup.get('isShow').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('isShow').patchValue(value);
                break;
        }
    }
    public get FONT_SIZE_MIN(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MIN;
    }

    public get FONT_SIZE_MAX(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MAX;
    }

    public get titleSettingsFormGroup(): TypographySettingsFormType['titleSettings'] {
        return this.typographySettingsFormGroup.get('titleSettings') as TypographySettingsFormType['titleSettings'];
    }

    public get descriptionSettingsFormGroup(): TypographySettingsFormType['descriptionSettings'] {
        return this.typographySettingsFormGroup.get(
            'descriptionSettings'
        ) as TypographySettingsFormType['descriptionSettings'];
    }

    public get priceSettingsFormGroup(): TypographySettingsFormType['priceSettings'] {
        return this.typographySettingsFormGroup.get('priceSettings') as TypographySettingsFormType['priceSettings'];
    }

    public get oldPriceSettingsFormGroup(): TypographySettingsFormType['oldPriceSettings'] {
        return this.typographySettingsFormGroup.get(
            'oldPriceSettings'
        ) as TypographySettingsFormType['oldPriceSettings'];
    }

    private setFontStyle(control: FormArray<FontStyleFormControl>, fontStyle: EFontStyle) {
        const index = control.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            control.removeAt(index);
        } else {
            control.push(this.fb.control(fontStyle));
        }
    }
}
